import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getCustomerTypes,
  getCustomerType,
  createCustomerType,
  editCustomerType,
  deleteCustomerType,
  getAllCustomerTypes,
  getTrashedCustomerTypes,
  restoreCustomerType,
} from "api/customerTypeApi"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const CUSTOMER_TYPE_QUERY_KEY = "customer-types"
export const CUSTOMER_TYPE_GROUP_QUERY_KEY = "customer-types-group"

// -------------------- FETCH PAGINATED --------------------
export const useCustomerTypes = (params = {}, enabled = true) => {
  return usePaginatedResource(
    CUSTOMER_TYPE_QUERY_KEY,
    getCustomerTypes,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomerType = (id, enabled = true) => {
  return useResourceItem(
    CUSTOMER_TYPE_QUERY_KEY,
    getCustomerType,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomerType = () => {
  return useCreateResource(createCustomerType, CUSTOMER_TYPE_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditCustomerType = () => {
  return useEditResource(editCustomerType, CUSTOMER_TYPE_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteCustomerType = () => {
  return useDeleteResource(deleteCustomerType, CUSTOMER_TYPE_QUERY_KEY)
}

// -------------------- ADDITIONAL --------------------

// Get all customer types without pagination
export const useAllCustomerTypes = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_TYPE_QUERY_KEY,
    getAllCustomerTypes,
    { ...params, all: true },
    true
  )
}

// Get trashed customer types
export const useTrashedCustomerTypes = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_TYPE_QUERY_KEY,
    getTrashedCustomerTypes,
    params,
    true
  )
}

// Restore a trashed customer type
export const useRestoreCustomerType = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomerType,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([CUSTOMER_TYPE_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([CUSTOMER_TYPE_QUERY_KEY, id], restoredItem)
    },
  })
}
